import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "@/hooks/useTheme";
import { isAgentsEnabled } from "@/lib/agents-flag";
import { AuthProvider } from "@/hooks/useAuth";
import { AppLayout } from "@/components/admin/app-layout";
import { ProtectedRoute } from "@/components/admin/protected-route";
import { RouteErrorBoundary } from "@/components/admin/route-error-boundary";
import { Toaster } from "@/components/application/notifications/toaster";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";

// Pages are lazy-loaded so each route gets its own chunk and the initial
// bundle stays small.
const AuthPage = lazy(() => import("@/pages/auth").then((m) => ({ default: m.AuthPage })));
const ForgotPasswordPage = lazy(() => import("@/pages/auth-forgot").then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import("@/pages/auth-reset").then((m) => ({ default: m.ResetPasswordPage })));
const DashboardPage = lazy(() => import("@/pages/dashboard").then((m) => ({ default: m.DashboardPage })));
const CustomersPage = lazy(() => import("@/pages/customers").then((m) => ({ default: m.CustomersPage })));
const CustomerUsersPage = lazy(() => import("@/pages/customer-users").then((m) => ({ default: m.CustomerUsersPage })));
const CustomerPoliciesPage = lazy(() => import("@/pages/customer-policies").then((m) => ({ default: m.CustomerPoliciesPage })));
const UsersPage = lazy(() => import("@/pages/users").then((m) => ({ default: m.UsersPage })));
const NewsletterPage = lazy(() => import("@/pages/newsletter").then((m) => ({ default: m.NewsletterPage })));
const ApplicationsPage = lazy(() => import("@/pages/applications").then((m) => ({ default: m.ApplicationsPage })));
const LeadsPage = lazy(() => import("@/pages/leads").then((m) => ({ default: m.LeadsPage })));
const GatedContentPage = lazy(() => import("@/pages/gated-content").then((m) => ({ default: m.GatedContentPage })));
const DownloadNotifyPage = lazy(() => import("@/pages/download-notify").then((m) => ({ default: m.DownloadNotifyPage })));
const HelpFeedbackPage = lazy(() => import("@/pages/help-feedback").then((m) => ({ default: m.HelpFeedbackPage })));
const InvitesPage = lazy(() => import("@/pages/invites").then((m) => ({ default: m.InvitesPage })));
const AccessRequestsPage = lazy(() => import("@/pages/access-requests").then((m) => ({ default: m.AccessRequestsPage })));
const SignupCodesPage = lazy(() => import("@/pages/signup-codes").then((m) => ({ default: m.SignupCodesPage })));
const ReviewHubPage = lazy(() => import("@/pages/reviews").then((m) => ({ default: m.ReviewHubPage })));
const MembersPage = lazy(() => import("@/pages/members").then((m) => ({ default: m.MembersPage })));
const RevenuePage = lazy(() => import("@/pages/revenue").then((m) => ({ default: m.RevenuePage })));
const AgentsPage = lazy(() => import("@/pages/agents").then((m) => ({ default: m.AgentsPage })));
const UsagePage = lazy(() => import("@/pages/usage").then((m) => ({ default: m.UsagePage })));
const SeoPage = lazy(() => import("@/pages/seo").then((m) => ({ default: m.SeoPage })));
const PlaygroundDataTypesPage = lazy(() => import("@/pages/playground-data-types").then((m) => ({ default: m.PlaygroundDataTypesPage })));
const SettingsPage = lazy(() => import("@/pages/settings").then((m) => ({ default: m.SettingsPage })));
const NotFoundPage = lazy(() => import("@/pages/not-found").then((m) => ({ default: m.NotFoundPage })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

function RouteFallback() {
  return (
    <div className="grid min-h-[60vh] place-items-center">
      <LoadingIndicator size="md" />
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <Toaster />
            <RouteErrorBoundary>
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  <Route path="/auth" element={<AuthPage />} />
                  <Route path="/auth/forgot" element={<ForgotPasswordPage />} />
                  <Route path="/auth/reset" element={<ResetPasswordPage />} />
                  <Route
                    element={
                      <ProtectedRoute>
                        <AppLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route path="/" element={<DashboardPage />} />
                    <Route path="/customers" element={<CustomersPage />} />
                    <Route path="/customers/:orgId/users" element={<CustomerUsersPage />} />
                    <Route path="/customers/:orgId/policies" element={<CustomerPoliciesPage />} />
                    <Route path="/users" element={<UsersPage />} />
                    <Route path="/newsletter" element={<NewsletterPage />} />
                    <Route path="/applications" element={<ApplicationsPage />} />
                    <Route path="/leads" element={<LeadsPage />} />
                    <Route path="/gated-content" element={<GatedContentPage />} />
                    <Route path="/download-notify" element={<DownloadNotifyPage />} />
                    <Route path="/help-feedback" element={<HelpFeedbackPage />} />
                    <Route path="/invites" element={<InvitesPage />} />
                    <Route path="/access-requests" element={<AccessRequestsPage />} />
                    <Route path="/signup-codes" element={<SignupCodesPage />} />
                    <Route path="/members" element={<MembersPage />} />
                    <Route path="/revenue" element={<RevenuePage />} />
                    <Route path="/usage" element={<UsagePage />} />
                    {isAgentsEnabled() && <Route path="/agents" element={<AgentsPage />} />}
                    <Route path="/seo" element={<SeoPage />} />
                    <Route path="/playground/data-types" element={<PlaygroundDataTypesPage />} />
                    <Route path="/reviews" element={<ReviewHubPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                  </Route>
                  <Route path="/404" element={<NotFoundPage />} />
                  <Route path="*" element={<Navigate to="/404" replace />} />
                </Routes>
              </Suspense>
            </RouteErrorBoundary>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
